import { LoadingButton } from "@mui/lab"
import { Alert, Box, Button, Divider, Grid, Typography } from "@mui/material"
import moment from "moment"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { AppCookies } from "../../../helpers/cookies"
import { getSignupFee, OrderRequest, postCreateOrder, ServiceType, SignupFees } from "../../../services/ntuc/memberService/payment"
import { getInitiatePayment } from "../../../services/ntuc/paymentService/payment"
import failureBanner from "../../../assets/payment_failure_banner.svg"
import PaymentAcknowledgementBanner from "../../../components/Acknowledgement/PaymentAcknowledgementBanner"
import { useSignupStore } from "../../../store/signup/UserSignupStore"
import { DeviceType } from "../../../services/ntuc/memberService/oaMembership"

function PaymentPage() {
    const { t } = useTranslation(`payment`)
    const [loading, setLoading] = useState(true)
    const [fees, setFees] = useState<SignupFees | null>(null)
    const [order, setOrder] = useState<OrderRequest | null>(null)
    const [membershipId, setMembershipId] = useState<number | null>(parseInt(sessionStorage[AppCookies.MEMBERSHIP_ID]))
    const [showErrorContent, setShowErrorContent] = useState<boolean>(false)
    const [state] = useSignupStore()

    const DECIMALS = 2

    useEffect(() => {
        setMembershipId(parseInt(sessionStorage[AppCookies.MEMBERSHIP_ID]))
    }, [sessionStorage])

    useEffect(() => {
        retrieveFees()
    }, [membershipId])

    useEffect(() => {
        if (state.membershipId !== undefined) {
            setMembershipId(state.membershipId)
        }
    }, [state.membershipId])

    useEffect(() => {
        if (fees == null || membershipId == null) return

        setOrder({
            feeType: fees.feeType,
            endDate: fees.endDate,
            startDate: fees.startDate,
            serviceType: ServiceType.SIGN_UP,
            noOfMonths: fees.month,
            amount: `${fees.amount}`,
            isOneTimePayment: true,
            isRecurringPayment: true,
            membershipId: membershipId,
            deviceType: DeviceType.WEB,
        })
    }, [fees, membershipId])

    const retrieveFees = () => {
        if (!membershipId) {
            setShowErrorContent(true)
            return
        }

        getSignupFee(membershipId)
            .then(response => response.data.content)
            .then(setFees)
            .catch(() => setShowErrorContent(true))
            .finally(() => setLoading(false))
    }

    const redirectToPaymentGateway = (clientOrderId: string) => {
        window.location.replace(getInitiatePayment(clientOrderId))
    }

    const handleOnPayClick = () => {
        if (order == null) return

        setLoading(true)

        postCreateOrder(order)
            .then(response => response.data.content.clientOrderId)
            .then(redirectToPaymentGateway)
            .catch(error => {
                console.error(error.response.data.content)
            })
            .then(() => setLoading(false))

    }

    const handleTryAgainClick = () => {
        retrieveFees()
    }

    const handleHomeClick = () => {
        if (window.myNtucConfig.homeUrl) {
            window.location.replace(window.myNtucConfig.homeUrl)
        }
    }

    const ErrorContent = () =>
        <Grid rowSpacing={3} container justifyContent="center" padding={2}>
            <PaymentAcknowledgementBanner bannerUrl={failureBanner} alt="Payment failure banner" />
            <Grid container item justifyContent="center" xs={12}>
                <Typography textAlign="center" variant="h2">{t(`failure.title`)}</Typography>
            </Grid>
            <Grid item justifyContent="center" xs={12}>
                <Typography textAlign="center">{t(`failure.body`)}</Typography>
            </Grid>
            <Grid container item xs={12} justifyContent="center" alignItems="center" columnSpacing={2} rowSpacing={3}>
                <Grid item xs={12} md={3} textAlign="center">
                    <Button onClick={handleTryAgainClick} variant="outlined" color="primary" size="large">
                        {t(`buttons.try_again`)}
                    </Button>
                </Grid>

                <Grid item xs={12} md={3} textAlign="center">
                    <Button onClick={handleHomeClick} variant="contained" color="secondary" size="large">
                        {t(`buttons.home`)}
                    </Button>
                </Grid>
            </Grid>
        </Grid>

    if (fees == null) {
        if (showErrorContent) {
            return <ErrorContent />
        } else {
            return <div>Unknown error</div>
        }
    } else {
        return (
            <Grid container>
                <Grid item xs={12} mb={3}>
                    <Box>
                        <Typography mb={3} variant="h3">{t(`title`)}</Typography>
                        <Typography>{t(`subtitle`)}</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} mb={3}>
                    <Typography variant="h4">{t(`details`)}</Typography>
                </Grid>
                <Grid item xs={10} mb={3}>
                    <Box>
                        <Typography>{t(`description`, { count: fees.month })}</Typography>
                        <Typography color="text.secondary">{moment(fees.startDate).format(`DD MMM YYYY`)} - {moment(fees.endDate).format(`DD MMM YYYY`)}</Typography>
                    </Box>
                </Grid>
                <Grid item xs={2} mb={3}>
                    <Typography textAlign="end">${fees.amount.toFixed(DECIMALS)}</Typography>
                </Grid>
                <Grid item xs={12} mb={1}>
                    <Divider />
                </Grid>
                <Grid item xs={10}>
                    <Typography>{t(`total`)}</Typography>
                </Grid>
                <Grid item xs={2} mb={3}>
                    <Typography textAlign="end">${fees.amount.toFixed(DECIMALS)}</Typography>
                </Grid>
                <Grid item xs={12} mb={3}>
                    <Alert severity="info">{t(`info`)}</Alert>
                </Grid>
                <Grid item xs={12} textAlign="center" marginBottom={2}>
                    <LoadingButton loading={loading} disabled={loading} onClick={handleOnPayClick} variant="contained" color="primary" size="large">
                        {t(`proceed_to_payment`)}
                    </LoadingButton>
                </Grid>
            </Grid>
        )
    }
}

export default PaymentPage
